import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const initialForm = {
  name: "",
  email: "",
  company: "",
  category: "Agro & Food Products",
  market: "",
  message: "",
};

export default function EnquiryDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const updateField = (event) => setForm({ ...form, [event.target.name]: event.target.value });

  const closeDialog = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) setConfirmation(null);
  };

  const submitEnquiry = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await api.post("/trade-enquiries", form);
      setConfirmation(response.data);
      setForm(initialForm);
      toast.success("Your trade enquiry has been received by UGI.");
    } catch (error) {
      toast.error(formatApiError(error, "The enquiry could not be sent. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent data-testid="trade-enquiry-dialog" className="max-h-[92vh] overflow-y-auto border-[#C5A059]/30 bg-[#FDFBF7] p-6 text-[#1C1613] sm:max-w-2xl sm:p-8">
        {confirmation ? (
          <div data-testid="enquiry-success-confirmation" className="py-10 text-center">
            <CheckCircle2 size={52} strokeWidth={1.2} className="mx-auto text-[#C5A059]" />
            <p className="mt-8 text-sm font-bold uppercase tracking-[0.28em] text-[#C5A059]">Enquiry Received</p>
            <h2 className="mt-4 font-serif text-4xl font-normal text-[#1C1613]">Thank you. UGI has received your enquiry.</h2>
            <p className="mx-auto mt-5 max-w-md text-sm leading-7 text-[#524842]">
              {confirmation.email_sent ? "The UGI team has also been notified by email." : "Your enquiry is saved for the UGI team to review."}
            </p>
            <button data-testid="enquiry-confirmation-close-button" onClick={() => closeDialog(false)} className="mt-9 bg-[#1C1613] px-7 py-4 text-xs font-bold tracking-[0.16em] text-[#FDFBF7]">
              CLOSE
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#C5A059]">Trade With UGI</p>
              <DialogTitle className="font-serif text-3xl font-normal tracking-[-0.02em]">Tell UGI what you're looking for.</DialogTitle>
              <DialogDescription className="text-sm leading-7 text-[#524842]">
                Share your sourcing or market-access requirement and UGI will review the opportunity.
              </DialogDescription>
            </DialogHeader>
            <form data-testid="trade-enquiry-form" onSubmit={submitEnquiry} className="mt-4 grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input data-testid="enquiry-name-input" id="name" name="name" value={form.name} onChange={updateField} required className="border-[#D8D0C3] bg-white" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Work Email</Label>
                <Input data-testid="enquiry-email-input" id="email" name="email" type="email" value={form.email} onChange={updateField} required className="border-[#D8D0C3] bg-white" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="company">Company</Label>
                <Input data-testid="enquiry-company-input" id="company" name="company" value={form.company} onChange={updateField} required className="border-[#D8D0C3] bg-white" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="category">Focus Area or Product Sector</Label>
                <select data-testid="enquiry-category-select" id="category" name="category" value={form.category} onChange={updateField} className="h-9 w-full rounded-md border border-[#D8D0C3] bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]">
                  <option>Agro & Food Products</option>
                  <option>Plastics</option>
                  <option>Leather & Leather Goods</option>
                  <option>Garments</option>
                  <option>Handicrafts</option>
                  <option>Chemicals</option>
                  <option>Electronics & Electricals</option>
                  <option>Something Else / Other Sector</option>
                </select>
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="market">Destination or Target Market</Label>
                <Input data-testid="enquiry-market-input" id="market" name="market" value={form.market} onChange={updateField} placeholder="For example: GCC, Africa, or another market" className="border-[#D8D0C3] bg-white" />
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="message">Requirement</Label>
                <Textarea data-testid="enquiry-message-textarea" id="message" name="message" value={form.message} onChange={updateField} required rows={5} placeholder="Tell UGI what you want to source, buy, or explore." className="resize-none border-[#D8D0C3] bg-white" />
              </div>
              <button data-testid="enquiry-submit-button" type="submit" disabled={submitting} className="mt-2 bg-[#1C1613] px-7 py-4 text-xs font-bold tracking-[0.16em] text-[#FDFBF7] transition-colors hover:bg-[#2A221E] disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2">
                {submitting ? "SENDING ENQUIRY..." : "SEND TRADE ENQUIRY"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
